import { isPathAllowedForRegion } from 'lib/region/regionPathAccess';
import { GIFTING_VOUCHER_PATH } from 'lib/gifting/path';

/**
 * A nav destination. Plain string for simple paths; object form when the link
 * needs a fragment — '/shop#addons' as a string breaks CustomLink's
 * region-prefixing (the first segment reads as "shop#addons") and encodes the
 * '#' into the path. The object form keeps `pathname` prefixable and `hash` a
 * real fragment, yielding e.g. '/in/shop#ring-gift-card'.
 */
export type NavHref =
  | string
  | { pathname: string; hash?: string; query?: Record<string, string> };

export type MenuLink = {
  labelKey: string;
  href: NavHref;
  /** Render only in these regions. */
  regionsOnly?: string[];
  /** Hide in these regions. */
  regionsExclude?: string[];
  /** Stronger weight / larger size — "see all" type entries. */
  emphasis?: boolean;
  /** Overrides `labelKey` per region, e.g. { US: 'header.foo.us' }. */
  regionLabelOverrides?: Record<string, string>;
};

export type MenuSection = {
  headingKey?: string;
  links: MenuLink[];
};

export type CtaVariant = 'solid' | 'outline' | 'text';

export type FeaturedCard = {
  labelKey: string;
  /** One-line supporting copy — rendered only by the showcase panel layout. */
  descriptionKey?: string;
  href: NavHref;
  image: string;
  /**
   * Square-native (720×720) render for the mobile drawer's card grid, which is
   * square. Falls back to `image` (the 462×240 desktop render) when unset.
   */
  imageMobile?: string;
  imageAlt?: string;
  /**
   * 'contain' (default): product render floats above the copy band.
   * 'cover': full-bleed photo behind the copy (the asset carries its own
   * bottom fade for legibility).
   */
  imageFit?: 'cover' | 'contain';
  /**
   * Photo plate with a transparent copy band. Tile + type follow the header
   * theme (`--hdr-pl-card-bg`); light keeps Figma's #F4F4F4.
   */
  lightPhoto?: boolean;
  /**
   * The DESKTOP `image` carries a baked-in light plate in both header themes
   * (e.g. AIR-gold), so the showcase card locks its overlaid copy to dark ink —
   * otherwise the dark theme paints white text on the white artwork. Only the
   * desktop mega-menu reads this: `imageMobile` renders are transparent PNGs on
   * a theme-frost tile, where the label must keep following the theme.
   */
  alwaysLightPhoto?: boolean;
  /** Desktop pill label. When set, the card is not itself a link. */
  ctaKey?: string;
  ctaVariant?: CtaVariant;
  /**
   * GTM v3 name. Rendered as `data-buttontype-v3`; `data-buttontype` stays
   * `header` so existing tags still fire. Also used on the mobile drawer card.
   */
  buttonType?: string;
  /** Second pill (desktop + Performance Lab mobile tall cards). */
  secondaryCta?: {
    labelKey: string;
    href: string;
    variant: CtaVariant;
    buttonType?: string;
    regionsOnly?: string[];
    regionsExclude?: string[];
  };
  /** Extra line under the description (e.g. "Open in Bengaluru."). */
  footnoteKey?: string;
  /** Coming Soon card: top-pinned badge + city list, not a full-card link. */
  comingSoon?: boolean;
  /** i18n key that resolves to a string[] of city names. */
  citiesKey?: string;
  regionLabelOverrides?: Record<string, string>;
  regionsOnly?: string[];
  /** Hide in these regions — for products sold almost everywhere (Ring AIR → US/PR). */
  regionsExclude?: string[];
};

/** Resolves a label key, honouring per-region overrides. */
export const resolveLabelKey = (
  item: { labelKey: string; regionLabelOverrides?: Record<string, string> },
  region: string,
): string => item.regionLabelOverrides?.[region] ?? item.labelKey;

const passesRegionGate = (
  item: { regionsOnly?: string[]; regionsExclude?: string[] },
  region: string,
): boolean => {
  const r = region.toUpperCase();
  if (item.regionsOnly && !item.regionsOnly.some((c) => c.toUpperCase() === r))
    return false;
  return !item.regionsExclude?.some((c) => c.toUpperCase() === r);
};

/** Featured-card region gate. `region` is the country code. */
export const isFeaturedCardVisible = (
  card: FeaturedCard,
  region: string,
): boolean => passesRegionGate(card, region);

/** Second-pill region gate — same rules as the card, applied to `secondaryCta`. */
export const isSecondaryCtaVisible = (
  cta: NonNullable<FeaturedCard['secondaryCta']>,
  region: string,
): boolean => passesRegionGate(cta, region);

/** Menu-link region gate — the card gate plus the route's own region access. */
export const isMenuLinkVisible = (link: MenuLink, region: string): boolean => {
  if (!passesRegionGate(link, region)) return false;
  const pathname =
    typeof link.href === 'string' ? link.href : link.href.pathname;
  return isPathAllowedForRegion(pathname, region.toUpperCase());
};

/** Where a top-level item renders. Default (unset) = both. */
export type NavSurface = 'desktop' | 'drawer';

type NavItemBase = {
  regionsOnly?: string[];
  regionsExclude?: string[];
  /** Limit to these surfaces; omit for desktop + drawer. */
  surfaces?: NavSurface[];
};

export type NavLinkItem = NavItemBase & {
  kind: 'link';
  key: string;
  labelKey: string;
  href: string;
};

export type NavMenuItem = NavItemBase & {
  kind: 'menu';
  key: string;
  labelKey: string;
  href?: string;
  /**
   * Renders the desktop panel as the showcase layout: large image cards with
   * descriptions, no link sections. Implied by `taglineKey`; set explicitly for
   * the intro-less variant.
   */
  showcase?: boolean;
  /** Showcase intro column (intro label + tagline) beside the cards. */
  taglineKey?: string;
  /** Overrides the intro column's label when it differs from the tab label. */
  introLabelKey?: string;
  featured?: FeaturedCard[];
  sections?: MenuSection[];
};

export type NavItem = NavLinkItem | NavMenuItem;

/** Top-level tab region gate (regionsOnly / regionsExclude). */
export const isNavItemVisible = (item: NavItem, region: string): boolean =>
  passesRegionGate(item, region);

export const isNavItemOnSurface = (
  item: NavItem,
  surface: NavSurface,
): boolean => !item.surfaces || item.surfaces.includes(surface);

const GIFT_CARDS_LINK: NavLinkItem = {
  kind: 'link',
  key: 'gift-cards',
  labelKey: 'header.v3.giftCards',
  href: GIFTING_VOUCHER_PATH,
};

/**
 * US desktop bar collapses Ring + Shop mega-menus into direct links and ends
 * with HSA-FSA. Shop stays drawer-only (mobile hamburger). HSA-FSA stays on the
 * desktop bar + mobile drawer, but is omitted from the desktop ☰ More primary
 * list (`morePrimary`). Gift Cards takes Performance Lab's slot in the US and
 * Shop's slot everywhere else; other regions otherwise keep `NAV_ITEMS`.
 */
export const resolveNavItems = (
  region: string,
): { desktop: NavItem[]; drawer: NavItem[]; morePrimary: NavItem[] } => {
  const isUS = region.toUpperCase() === 'US';

  const items: NavItem[] = NAV_ITEMS.filter((item) =>
    isNavItemVisible(item, region),
  ).flatMap((item) => {
    if (!isUS) return item.key === 'shop' ? [GIFT_CARDS_LINK] : [item];
    if (item.key === 'performance-lab') return [GIFT_CARDS_LINK];
    if (item.key === 'smart-rings') {
      return [
        {
          kind: 'link',
          key: 'ring-pro',
          labelKey: 'header.v3.ringPro',
          href: '/ring-pro/buy',
        } satisfies NavLinkItem,
      ];
    }
    if (item.key === 'shop') {
      return [
        {
          kind: 'link',
          key: 'shop',
          labelKey: 'header.v3.shop',
          href: '/shop',
          surfaces: ['drawer'],
        } satisfies NavLinkItem,
      ];
    }
    return [item];
  });

  if (isUS) {
    items.push({
      kind: 'link',
      key: 'hsa-fsa',
      labelKey: 'header.v3.hsaFsa',
      href: '/hsa-fsa',
    });
  }

  const desktop = items.filter((item) => isNavItemOnSurface(item, 'desktop'));
  const drawer = items.filter((item) => isNavItemOnSurface(item, 'drawer'));
  // Desktop ☰ More primary: drawer list minus HSA-FSA (already on the bar).
  const morePrimary = drawer.filter((item) => item.key !== 'hsa-fsa');

  return { desktop, drawer, morePrimary };
};

/**
 * Desktop/mobile showcase intro (label + tagline). Shop only shows it when
 * Gift Ring is filtered out (fewer than 3 cards); IN keeps the 3-up with no
 * intro. Everything else shows an intro whenever `taglineKey` is set.
 */
export const hasShowcaseIntro = (
  item: NavMenuItem,
  visibleCardCount: number,
): boolean => {
  if (!item.taglineKey) return false;
  if (item.key === 'shop') return visibleCardCount < 3;
  return true;
};

/**
 * Reserved `openKey` for the desktop hamburger's "More" panel — it opens
 * `MORE_MENU_SECTIONS` rather than a single tab, so it needs a key that can
 * never collide with a real nav item's `key`.
 */
export const MORE_MENU_KEY = '__more__';

const navbarV3 = (name: string) => `web_v2/home-v4/navbar/v3/${name}.png`;
const navbarMinV2 = (name: string) =>
  `web_v2/home-v4/navbar/min-v2/${name}.png`;

/**
 * The hamburger "More" panel — the company/audience axis, two labelled link
 * columns. Column order (Science & Research → Solutions) also feeds the
 * mobile drawer's flat "More" zone.
 *
 * `/partners` and the creators typeform carry `regionsExclude: IN/US/PR`: on
 * `main` they lived behind the Partnerships dropdown, which was hidden in those
 * three regions (HEADER_V2_MIGRATION.md §2 A10). V2 dropped that gate; it is
 * restored here as data so the desktop panel and mobile drawer agree.
 */
const PARTNERSHIPS_HIDDEN_REGIONS = ['IN', 'US', 'PR'];

export const MORE_MENU_SECTIONS: MenuSection[] = [
  {
    headingKey: 'header.v3.menu.more.scienceResearch.heading',
    links: [
      {
        labelKey: 'header.v3.menu.more.scienceResearch.science',
        href: '/science',
      },
      {
        labelKey: 'header.v3.menu.more.scienceResearch.researchStudies',
        href: '/science/studies',
      },
      {
        labelKey: 'header.v3.menu.more.scienceResearch.pulsomics',
        href: '/pulsomics',
      },
      {
        labelKey: 'header.v3.menu.more.scienceResearch.explainers',
        href: '/science/explainers',
      },
    ],
  },
  {
    headingKey: 'header.v3.menu.more.solutions.heading',
    links: [
      {
        labelKey: 'header.v3.menu.more.solutions.forBusiness',
        href: '/partners',
        regionsExclude: PARTNERSHIPS_HIDDEN_REGIONS,
      },
      {
        labelKey: 'header.v3.menu.more.solutions.forCreators',
        href: 'https://form.typeform.com/to/RIksJbSH',
        regionsExclude: PARTNERSHIPS_HIDDEN_REGIONS,
      },
      {
        labelKey: 'header.v3.menu.more.solutions.forDevelopers',
        href: '/ultrasignal',
      },
      {
        labelKey: 'header.v3.menu.more.solutions.ultraWork',
        href: '/for-work',
      },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = [
  {
    // Ultrahuman Ring — showcase panel: intro column beside two product cards
    // carrying the BUY links.
    kind: 'menu',
    key: 'smart-rings',
    labelKey: 'header.v3.smartRings',
    taglineKey: 'header.v3.menu.smartRings.tagline',
    introLabelKey: 'header.v3.menu.smartRings.introLabel',
    featured: [
      {
        labelKey: 'header.v3.menu.smartRings.featured.ringPro',
        descriptionKey: 'header.v3.menu.smartRings.featured.ringProDesc',
        href: '/ring-pro/buy',
        image: navbarV3('PRO'),
        imageMobile: navbarMinV2('Ring-Pro'),
        ctaKey: 'header.buyNow',
        ctaVariant: 'outline',
        buttonType: 'header_ring_pro_buy_button',
        secondaryCta: {
          labelKey: 'header.explore',
          href: '/ring-pro',
          variant: 'text',
          buttonType: 'header_ring_pro_learn_button',
        },
      },
      {
        labelKey: 'header.v3.menu.smartRings.featured.ringAir',
        descriptionKey: 'header.v3.menu.smartRings.featured.ringAirDesc',
        href: '/ring/buy',
        image: navbarV3('Ring-Gold-1'),
        imageMobile: navbarMinV2('Ring-AIR-gold'),
        ctaKey: 'header.buyNow',
        ctaVariant: 'outline',
        buttonType: 'header_ring_air_buy_button',
        secondaryCta: {
          labelKey: 'header.explore',
          href: '/ring',
          variant: 'text',
          buttonType: 'header_ring_air_learn_button',
        },
        // Not sold in the United States or Puerto Rico (PR uses the US store).
        regionsExclude: ['US', 'PR'],
      },
    ],
  },
  {
    // Advanced Markers — three product cards carrying the BUY links, no intro.
    kind: 'menu',
    key: 'advanced-markers',
    labelKey: 'header.v3.advancedMarkers',
    showcase: true,
    featured: [
      {
        labelKey: 'header.v3.menu.advancedMarkers.featured.cgm',
        descriptionKey: 'header.v3.menu.advancedMarkers.featured.cgmDesc',
        href: '/pricing',
        image: navbarV3('M1'),
        imageMobile: navbarMinV2('Continuous-Glucose-Monitoring'),
        ctaKey: 'header.buyNow',
        ctaVariant: 'outline',
        buttonType: 'header_cgm_buy_button',
      },
      {
        labelKey: 'header.v3.menu.advancedMarkers.featured.bloodVision',
        descriptionKey:
          'header.v3.menu.advancedMarkers.featured.bloodVisionDesc',
        href: '/blood-vision/buy',
        image: navbarV3('BV'),
        imageMobile: navbarMinV2('Blood-Vision'),
        ctaKey: 'header.buyNow',
        ctaVariant: 'outline',
        buttonType: 'header_blood_vision_buy_button',
      },
      {
        labelKey: 'header.v3.menu.advancedMarkers.featured.home',
        descriptionKey: 'header.v3.menu.advancedMarkers.featured.homeDesc',
        href: '/home/buy',
        image: navbarV3('Home'),
        imageMobile: navbarMinV2('Home'),
        ctaKey: 'header.buyNow',
        ctaVariant: 'outline',
        buttonType: 'header_home_buy_button',
      },
    ],
  },
  {
    // Performance Lab — every geo gets Explore + Coming Soon. Book a visit
    // stays a pill on Explore in India only (same gate as main's card).
    kind: 'menu',
    key: 'performance-lab',
    labelKey: 'header.v3.performanceLab',
    taglineKey: 'header.v3.menu.performanceLab.tagline',
    featured: [
      {
        labelKey: 'header.v3.menu.performanceLab.explore',
        descriptionKey: 'header.v3.menu.performanceLab.exploreDesc',
        footnoteKey: 'header.v3.menu.performanceLab.exploreOpenIn',
        href: '/performance-lab',
        image: navbarV3('explore'),
        imageMobile: navbarMinV2('explore_mbl'),
        imageFit: 'cover',
        lightPhoto: true,
        ctaKey: 'header.explore',
        ctaVariant: 'outline',
        buttonType: 'header_performance_lab_explore_button',
        secondaryCta: {
          labelKey: 'header.v3.menu.performanceLab.bookVisit',
          href: '/performance-lab/contact',
          variant: 'outline',
          buttonType: 'header_performance_lab_book_button',
          regionsOnly: ['IN'],
        },
      },
      {
        labelKey: 'header.v3.menu.performanceLab.comingSoon',
        href: '/performance-lab/partner-with-us',
        image: navbarV3('cs'),
        imageMobile: navbarMinV2('cs_mbl'),
        imageFit: 'cover',
        lightPhoto: true,
        comingSoon: true,
        citiesKey: 'header.v3.menu.performanceLab.comingSoonCities',
        ctaKey: 'header.v3.menu.performanceLab.partnerWithUs',
        ctaVariant: 'outline',
        buttonType: 'header_performance_lab_partner_button',
      },
    ],
  },
  {
    // Shop — three cards, intro only when Gift Ring is hidden (hasShowcaseIntro).
    kind: 'menu',
    key: 'shop',
    labelKey: 'header.v3.shop',
    showcase: true,
    taglineKey: 'header.v3.menu.shop.tagline',
    introLabelKey: 'header.v3.shop',
    featured: [
      {
        labelKey: 'header.v3.menu.shop.shopAll',
        descriptionKey: 'header.v3.menu.shop.shopAllDesc',
        href: '/shop',
        image: navbarV3('Accesssories'),
        imageMobile: navbarMinV2('Shop%20all'),
        ctaKey: 'header.explore',
        ctaVariant: 'outline',
        buttonType: 'header_shop_all_button',
      },
      {
        labelKey: 'header.v3.menu.shop.ultrahumanX',
        descriptionKey: 'header.v3.menu.shop.ultrahumanXDesc',
        href: '/x',
        image: navbarV3('UHX'),
        imageMobile: navbarMinV2('UltrahumanX'),
        ctaKey: 'header.explore',
        ctaVariant: 'outline',
        buttonType: 'header_uhx_explore_button',
      },
      {
        // India-only (matches the AllProducts Gift Card tile).
        labelKey: 'header.v3.menu.shop.giftRing',
        descriptionKey: 'header.v3.menu.shop.giftRingDesc',
        href: { pathname: '/shop', hash: 'ring-gift-card' },
        image: navbarV3('Gift-Card'),
        imageMobile: navbarMinV2('Gift-Ultrahuman-Ring'),
        ctaKey: 'header.explore',
        ctaVariant: 'outline',
        buttonType: 'header_gift_ring_button',
        regionsOnly: ['IN'],
      },
    ],
  },
];
